/// <amd-module name="Build/DesignerExtensions/ExtendsCheckPanel/Components/ExtendsCheckEditPanel" />
import React = require("react");
import { IExtendsCheck } from "Build/DesignerExtensions/ExtendsCheckPanel/Components/ExtendsCheckConfig";
import * as Resources from "Build/DesignerExtensions/ExtendsCheckPanel/Resources";
import { ObservableValue, IReadonlyObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";
import { Observer } from "VSSUI/Observer";
import { IButtonProps } from "VSSUI/Button";
import { FormItem } from "VSSUI/FormItem";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner } from "VSSUI/Spinner";
import { TextField } from "VSSUI/TextField";
import * as RefUtil from "Build/DesignerExtensions/ExtendsCheckPanel/Components/Ref";

export interface IExtendsCheckEditPanelProps {
    check: IExtendsCheck;
    isNewCheck: boolean;
    showOverlay: IReadonlyObservableValue<boolean>;
    onDismiss: (check?: IExtendsCheck) => void;
}

interface IRepositoryType {
    id: string;
    name: string;
    iconClass: string;
}

export class ExtendsCheckEditPanel extends VssComponent<IExtendsCheckEditPanelProps> {

    public constructor(props: IExtendsCheckEditPanelProps) {
        super(props);
        this._repoTypeObservable = new ObservableValue<string>(props.check.repositoryType);
        this._repositoryObservable = new ObservableValue<string>(props.check.repositoryName);
        this._isRepositoryInvalidObservable = new ObservableValue<boolean>(false);
        this._repositoryMessageObservable = new ObservableValue<string>(Resources.RepositoryExample);
        this._repositoryRefObservable = new ObservableValue<string>(props.check.repositoryRef);
        this._isRepositoryRefInvalidObservable = new ObservableValue<boolean>(false);
        this._repositoryRefMessageObservable = new ObservableValue<string>(Resources.RepositoryRefExample);
        this._templatePathObservable = new ObservableValue<string>(props.check.templatePath);
        this._isTemplatePathInvalidObservable = new ObservableValue<boolean>(false);
        this._templatePathMessageObservable = new ObservableValue<string>(Resources.TemplatePathExample);
        this._areFieldsValidObservable = new ObservableValue<boolean>(false);
    }

    public render() {
        return (
            <Observer valid={this._areFieldsValidObservable} showOverlay={this.props.showOverlay}>
                {(props: { valid: boolean; showOverlay: boolean }) => {
                    const { onDismiss } = this.props;
                    const buttons: IButtonProps[] = [
                        {
                            onClick: () => {
                                onDismiss();
                            },
                            text: Resources.Cancel
                        },
                        {
                            onClick: this.submit,
                            disabled: !props.valid,
                            primary: true,
                            text: this.props.isNewCheck ? Resources.AddTemplateButton : Resources.SaveTemplateButton
                        }
                    ];
                    return (
                        <Panel
                            defaultActiveElement={".repository-input"}
                            lightDismiss={false}
                            onDismiss={() => {
                                onDismiss();
                            }}
                            backButtonProps={{ onClick: () => onDismiss() }}
                            overlayContent={props.showOverlay ? <Spinner /> : undefined}
                            titleProps={{ text: Resources.RequiredYamlTemplate }}
                            footerButtonProps={buttons}
                        >
                            <div className="rhythm-vertical-16 flex-grow">
                                <FormItem label={Resources.RepositoryType}>
                                    <RadioButtonGroup
                                            onSelect={selectedId => this._onRepoTypeSelectionChange(selectedId)}
                                            selectedButtonId={this._repoTypeObservable}
                                        >
                                        <RadioButton id={this._repositoryTypes[0].id} text={this._repositoryTypes[0].name} key={this._repositoryTypes[0].id} />
                                        <RadioButton id={this._repositoryTypes[1].id} text={this._repositoryTypes[1].name} key={this._repositoryTypes[1].id} />
                                        <RadioButton id={this._repositoryTypes[2].id} text={this._repositoryTypes[2].name} key={this._repositoryTypes[2].id} />
                                        <RadioButton id={this._repositoryTypes[3].id} text={this._repositoryTypes[3].name} key={this._repositoryTypes[3].id} />
                                    </RadioButtonGroup>
                                </FormItem>
                                <FormItem label={Resources.Repository} error={this._isRepositoryInvalidObservable} message={this._repositoryMessageObservable}>
                                    <TextField value={this._repositoryObservable} onChange={this._onRepoNameChange} inputClassName="repository-input" />
                                </FormItem>
                                <FormItem label={Resources.RepositoryRef} error={this._isRepositoryRefInvalidObservable} message={this._repositoryRefMessageObservable}>
                                    <TextField value={this._repositoryRefObservable} onChange={this._onRepositoryRefChange} />
                                </FormItem>
                                <FormItem label={Resources.TemplatePath} error={this._isTemplatePathInvalidObservable} message={this._templatePathMessageObservable}>
                                    <TextField value={this._templatePathObservable} onChange={this._onTemplatePathChange} />
                                </FormItem>
                            </div>
                        </Panel>
                    );
                }}
            </Observer>
        );
    }

    private _onRepoNameChange = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        this._repositoryObservable.value = newValue;
        this._validate();
    }

    private _onRepositoryRefChange = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        this._repositoryRefObservable.value = newValue;
        this._validate();
    }

    private _onTemplatePathChange = (e: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        this._templatePathObservable.value = newValue;
        this._validate();
    }

    private _onRepoTypeSelectionChange = (selectedId: string) => {
        this._repoTypeObservable.value = selectedId;
        this._validate();
    }

    private submit = (): void => {
        const check = this._getCheckFromForm();
        this.props.onDismiss(check);
    };

    private _validate() {
        const check = this._getCheckFromForm();
        if (!check) {
            this._areFieldsValidObservable.value = false;
            return;
        }

        // If some of the fields aren't filled in yet, don't bother showing error messages, just disable the save button
        if (!check.repositoryName ||
            !check.repositoryRef ||
            !check.repositoryType ||
            !check.templatePath) {
            this._areFieldsValidObservable.value = false;
            this._repositoryMessageObservable.value = Resources.RepositoryExample;
            this._repositoryRefMessageObservable.value = Resources.RepositoryRefExample;
            this._templatePathMessageObservable.value = Resources.TemplatePathExample;
            return;
        }

        // Check each field and report any errors found

        // Make sure the repository name field is of the correct form (org/repo or for git project/repo or repo)
        var slashCount = (check.repositoryName.match(/\//g) || []).length;
        if (check.repositoryType !== "git" && slashCount !== 1) {
            this._isRepositoryInvalidObservable.value = true;
            this._repositoryMessageObservable.value = Resources.RepositoryRequiredOrgFormat;
        }
        else if (check.repositoryType === "git" && slashCount > 1) {
            this._isRepositoryInvalidObservable.value = true;
            this._repositoryMessageObservable.value = Resources.RepositoryRequiredProjectFormat;
        }
        else {
            // reset the variables to the default values
            this._isRepositoryInvalidObservable.value = false;
            this._repositoryMessageObservable.value = Resources.RepositoryExample;
        }

        // Validate that the ref is a Tag or a branch or has a wildcard
        let refName = this._repositoryRefObservable.value;
        let refError: string | undefined = undefined;
        if (refName.indexOf('*') >= 0) {
            // if it contains a wildcard, we don't try to do validation
        }
        else if (RefUtil.isTag(refName)) {
            refError = RefUtil.getInvalidTagNameMessage(refName);
        }
        else {
            refName = RefUtil.branchNameToRef(refName);
            refError = RefUtil.getInvalidBranchNameMessage(refName);
        }
        this._isRepositoryRefInvalidObservable.value = refError ? true : false;
        this._repositoryRefMessageObservable.value = refError || Resources.RepositoryRefExample;

        // Validate the path
        const path = this._templatePathObservable.value;
        if (path.indexOf('*') >= 0) {
            // if it contains a wildcard, we don't try to do validation
            this._isTemplatePathInvalidObservable.value = false;
            this._templatePathMessageObservable.value = Resources.TemplatePathExample;
        }
        else {
            const pathError = this._getInvalidFileNameMessage(path);
            this._isTemplatePathInvalidObservable.value = pathError ? true : false
            this._templatePathMessageObservable.value = pathError || Resources.TemplatePathExample;
        }

        // compute the "are fields valid" value
        this._areFieldsValidObservable.value = 
            !this._isRepositoryInvalidObservable.value &&
            !this._isRepositoryRefInvalidObservable.value &&
            !this._isTemplatePathInvalidObservable.value;
    }

    private _getCheckFromForm(): IExtendsCheck {
        const newCheck: IExtendsCheck = {
            repositoryType: this._repoTypeObservable.value,
            repositoryName: this._repositoryObservable.value,
            repositoryRef: this._repositoryRefObservable.value,
            templatePath: this._templatePathObservable.value
        }
        return newCheck;
    }

     // Determine if the given filename is a legal git file name and return undefined if so,
     // otherwise a message that indicates the reason why it is invalid.
    private _getInvalidFileNameMessage(name: string): string | undefined {
        if (Array.from(name).some(c => this._invalidFilePathChars.has(c))) {
            return format(Resources.FileInvalidCharactersFormat, name);
        } else if (name.endsWith(".") || name.endsWith(" ")) {
            return format(Resources.FileInvalidEndCharactersFormat, name);
        }
    
        return undefined;
    }

    private _invalidFilePathChars = new Set(['"', ":", "<", ">", "\\", "*", "?"]);

    private _repositoryTypes: IRepositoryType[] = [
        { id: "git", name: Resources.AzureReposGit, iconClass: "ms-Icon--GitLogo" },
        { id: "github", name: Resources.GitHub, iconClass: "ms-Icon--GitHubLogo" },
        { id: "githubenterprise", name: Resources.GitHubEnterprise, iconClass: "ms-Icon--GitHubLogo" },
        { id: "bitbucket", name: Resources.Bitbucket, iconClass: "ms-Icon--BitbucketLogo32" }
    ];

    private _repoTypeObservable: ObservableValue<string>;
    private _repositoryObservable: ObservableValue<string>;
    private _isRepositoryInvalidObservable: ObservableValue<boolean>;
    private _repositoryMessageObservable: ObservableValue<string>;
    private _repositoryRefObservable: ObservableValue<string>;
    private _isRepositoryRefInvalidObservable: ObservableValue<boolean>;
    private _repositoryRefMessageObservable: ObservableValue<string>;
    private _templatePathObservable: ObservableValue<string>;
    private _isTemplatePathInvalidObservable: ObservableValue<boolean>;
    private _templatePathMessageObservable: ObservableValue<string>;
    private _areFieldsValidObservable: ObservableValue<boolean>;
}
